import React, { useState, useEffect, useContext } from "react";
import FormattedNumberInput from "../../../components/Shared/FormattedNumberInput";
import { useNavigate } from "react-router-dom";
import TrustedDevelopers from "./TrustedDevelopers";
import "../HomePage.css";
import Select from "react-select";
import {
  FaHome,
  FaBuilding,
  FaShoppingCart,
  FaKey,
  FaHouseUser,
  FaStore,
  FaCoins,
  FaSearch,
  FaPlus,
  FaEnvelope,
  FaFire,
  FaCity,
  FaMapPin,
  FaGlobeAfrica,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslateKey } from "../../../components/translation/useTranslateKey";
import { LangContext } from "../../../components/translation/langugeContext";

const Hero = ({ pageData, openContactModal, openAddUnitModal }) => {
  const t = useTranslateKey();
  const { lang } = useContext(LangContext);

  // State variables to manage user selections and inputs
  const [propertyStatus, setPropertyStatus] = useState("resale"); // Default property status
  const [propertyIntent, setPropertyIntent] = useState("buy"); // Default property intent
  const [propertyCategory, setPropertyCategory] = useState(1); // Default property category
  const [selectedCountry, setSelectedCountry] = useState(""); // country ID
  const [selectedCity, setSelectedCity] = useState(""); // Selected city ID
  const [selectedArea, setSelectedArea] = useState(""); // Selected area ID
  const [selectedPropertyType, setSelectedPropertyType] = useState(""); // Selected property type
  const [minPrice, setMinPrice] = useState(0); // Minimum price input
  const [maxPrice, setMaxPrice] = useState(0); // Maximum price input
  const navigate = useNavigate();

  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);

  const selectedCategory = pageData?.property_categories?.find(
    //to simple the code in jsx
    (cat) => cat.id === propertyCategory
  );
  const handleBrowseClick = (event) => {
    event.preventDefault(); // Prevent default form submission

    // Get the selected category object
    const category = pageData.property_categories.find(
      (cat) => cat.id === propertyCategory
    );

    // If no category found (edge case), just return
    if (!category) return;

    // Get all type_en values from the selected category
    const defaultTypes = category.types.map((type) => type.id);

    // Use selected type if one is chosen, otherwise use all types of the selected category
    const selectedTypes = selectedPropertyType
      ? [selectedPropertyType]
      : defaultTypes;

    // Construct filters object
    const filters = {
      property_status: propertyStatus,
      property_intent: propertyIntent,
      new_property_category: propertyCategory, // now an ID
      country: selectedCountry,
      city: selectedCity,
      area: selectedArea,
      min_price: minPrice,
      max_price: maxPrice || 100000000,
    };

    // Build URL search params
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        if (
          (key === "min_price" && Number(value) === 0) ||
          (key === "max_price" && Number(value) === 100000000)
        ) {
          return; // skip default price values
        }
        params.append(key, value);
      }
    });

    // Append each selected type to the query string
    selectedTypes.forEach((type) => params.append("new_property_type", type));

    // Navigate to the property browser with filters
    const queryString = params.toString();
    navigate(`/property-browser?${queryString}`);
  };

  useEffect(() => {
    if (selectedCountry) {
      const cities = pageData.normalized_cities.filter(
        (city) => city.country_id.toString() === selectedCountry
      );
      setFilteredCities(cities);
      setSelectedCity("");
      setSelectedArea("");
      setFilteredAreas([]);
    } else {
      setFilteredCities([]);
      setFilteredAreas([]);
    }
  }, [selectedCountry, pageData.normalized_cities]);

  useEffect(() => {
    if (selectedCity) {
      const areas = pageData.normalized_areas.filter(
        (area) => area.city_id.toString() === selectedCity
      );
      setFilteredAreas(areas);
      setSelectedArea("");
    } else {
      setFilteredAreas([]);
    }
  }, [selectedCity, pageData.normalized_areas]);

  useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration
      once: true, // Whether animation should happen only once
    });
  }, []);

  // Handle change in property type selection
  const handlePropertyTypeChange = (e) => {
    setSelectedPropertyType(e.target.value); // Update state with selected property type
  };

  // Update options to use translations
  const countryOptions = [
    { value: "", label: t("selectCountry") },
    ...pageData.normalized_countries.map((country) => ({
      value: country.id.toString(),
      label: lang === "ar" ? country.name_ar : country.name_en,
    })),
  ];

  const cityOptions = [
    { value: "", label: t("selectCity") },
    ...filteredCities.map((city) => ({
      value: city.id.toString(),
      label: lang === "ar" ? city.name_ar : city.name_en,
    })),
  ];

  const propertyTypeOptions = selectedCategory
    ? [
        {
          value: "",
          label: t("selectType"),
        },
        {
          label: lang === "ar" ? selectedCategory.category_ar : selectedCategory.category_en,
          options: selectedCategory.types.map((type) => ({
            value: type.id.toString(),
            label: lang === "ar"
              ? type.type_ar
              : type.type_en.charAt(0).toUpperCase() + type.type_en.slice(1),
          })),
        },
      ]
    : [];

  return (
    <section id="home" className="relative min-h-screen pt-0">
      <div className=" layers absolute inset-0 bg-gradient-to-b from-transparent to-background/80 "></div>

      <div className="relative flex items-center justify-center min-h-[calc(100vh-60px)] text-center z-10">
        <div
          className="container mx-auto px-3 sm:px-4"
          data-aos="fade-up"
          data-aos-disable="true"
          data-aos-enable="md"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 shadow-lg max-w-6xl mx-auto">
            <div className="md:block mb-4 mt-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1">
                <span className="text-white">{t("DiscoverYourDreamHome")}</span>
              </h1>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2">
                <img
                  src="./img/logo3copy.png"
                  alt="Signature Logo"
                  className="inline-block h-12 md:h-16"
                />
                <span style={{ color: "white" }}> {t("oflive")}</span>
              </h2>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-300">
                {t("FindYourPerfectProperty")}
              </p>
            </div>

            {/* Property Search */}
            <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-lg mb-4">
              {/* Property Type Toggles */}
              <div className="flex flex-wrap justify-between gap-3 mb-4">
                {/* Primary/Resale Toggle */}
                <div className="bg-[#11132F]/50 backdrop-blur-lg rounded-lg flex items-center p-1">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="propertyStatus"
                      id="resale"
                      className="hidden"
                      checked={propertyStatus === "resale"}
                      onChange={() => setPropertyStatus("resale")}
                    />
                    <label
                      htmlFor="resale"
                      className={`property-toggle-btn cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-4 transition-all duration-300 ${
                        propertyStatus === "resale" ? "primary-active" : ""
                      }`}
                    >
                      <FaHome />
                      {t("Resale")}
                    </label>
                    <input
                      type="radio"
                      name="propertyStatus"
                      id="primary"
                      className="hidden"
                      checked={propertyStatus === "primary"}
                      onChange={() => setPropertyStatus("primary")}
                    />
                    <label
                      htmlFor="primary"
                      className={`property-toggle-btn cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-4 transition-all duration-300 ${
                        propertyStatus === "primary" ? "primary-active" : ""
                      }`}
                    >
                      <FaBuilding />
                      {t("Primary")}
                    </label>
                  </div>
                </div>

                {/* Buy/Rent Toggle */}
                <div className="bg-[#11132F]/50 backdrop-blur-lg rounded-lg flex items-center p-1">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="propertyIntent"
                      id="buy"
                      className="hidden"
                      checked={propertyIntent === "buy"}
                      onChange={() => setPropertyIntent("buy")}
                    />
                    <label
                      htmlFor="buy"
                      className={`property-toggle-btn cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-4 transition-all duration-300 ${
                        propertyIntent === "buy" ? "primary-active" : ""
                      }`}
                    >
                      <FaShoppingCart />
                      {t("Buy")}
                    </label>
                    <input
                      type="radio"
                      name="propertyIntent"
                      id="rent"
                      className="hidden"
                      checked={propertyIntent === "rent"}
                      onChange={() => setPropertyIntent("rent")}
                    />
                    <label
                      htmlFor="rent"
                      className={`property-toggle-btn cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-4 transition-all duration-300 ${
                        propertyIntent === "rent" ? "primary-active" : ""
                      }`}
                    >
                      <FaKey />
                      {t("Rent")}
                    </label>
                  </div>
                </div>

                {/* Residential/Commercial Toggle */}
                <div className="bg-[#11132F]/50 backdrop-blur-lg rounded-lg flex items-center p-1 mb-4">
                  <div className="flex items-center">
                    {pageData.property_categories?.map((category) => (
                      <React.Fragment key={category.id}>
                        <input
                          type="radio"
                          name="propertyCategory"
                          id={`category-${category.id}`}
                          className="hidden"
                          checked={propertyCategory === category.id}
                          onChange={() => {
                            setPropertyCategory(category.id);
                            setSelectedPropertyType(""); // Reset type when category changes
                          }}
                        />
                        <label
                          htmlFor={`category-${category.id}`}
                          className={`property-toggle-btn cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-4 transition-all duration-300 ${
                            propertyCategory === category.id
                              ? "primary-active"
                              : ""
                          }`}
                        >
                          {/* Show icons based on category_en */}
                          {category.category_en === "Residential" ? (
                            <FaHouseUser />
                          ) : (
                            <FaStore />
                          )}
                          {lang === "ar" ? category.category_ar : category.category_en}
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/*  form */}
              <form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ">
                {/* Location Field */}
                <div className="search-field-group ">
                  <label className="search-label block mb-1 text-sm">
                    <FaGlobeAfrica className="inline mr-1" />
                    <span>{t("country")}</span>
                  </label>
                  <div className="py-0 mt-0">
                    <Select
                      className=" w-full "
                      classNamePrefix="react-select"
                      options={countryOptions}
                      placeholder={t("selectCountry")}
                      value={countryOptions.find(
                        (opt) => opt.value === selectedCountry
                      )}
                      onChange={(selectedOption) => {
                        if (selectedOption?.value !== t("selectCountry")) {
                          setSelectedCountry(selectedOption.value);
                          setSelectedCity("");
                          setSelectedArea("");
                        }
                      }}
                      isSearchable={false}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused
                            ? "rgba(255, 255, 255, 0.15)"
                            : "#11132F",
                          border: state.isFocused
                            ? "1px solid #f9c835"
                            : "1px solid #332d7e",
                          borderRadius: 8,
                          transition: "all 0.3s ease",
                          color: "#fff",
                          height: "53px",
                          outline: "none",
                          paddingLeft: "16px",
                          paddingRight: "40px",
                          display: "flex",
                          alignItems: "center",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: state.isFocused
                              ? "rgba(255, 255, 255, 0.15)"
                              : "rgba(0, 0, 0, 0.3)",
                            borderColor: state.isFocused
                              ? "#f9c835"
                              : "rgba(255, 255, 255, 0.2)",
                          },
                        }),
                        dropdownIndicator: (base, state) => ({
                          ...base,
                          color: "#aaa",
                          position: "absolute",
                          right: "12px",
                          padding: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),

                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#11132F",
                          border: "1px solid #f9c835",
                          borderRadius: 8,
                          marginTop: 4,
                          color: "white",
                          zIndex: 9999,
                        }),
                        menuList: (provided) => ({
                          ...provided,

                          scrollbarColor: "#007ACC transparent", // scroller color
                          scrollbarWidth: "thin",
                        }),
                        option: (base, state) => ({
                          ...base,

                          cursor: "pointer",
                          color: state.data.value === "" ? "#fff" : "#fff",

                          backgroundColor: state.isFocused
                            ? "#007ACC"
                            : "#11132f",
                          textAlign: "left", // text left
                          paddingLeft: "12px",
                          ":active": {
                            backgroundColor: "#007ACC",
                          },
                        }),

                        singleValue: (base, { data }) => ({
                          ...base,
                          color: data.value === "" ? "#fff" : "#fff",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#fff",
                          marginLeft: 0,
                          paddingLeft: 0,
                        }),

                        valueContainer: (base) => ({
                          ...base,
                          paddingLeft: "0px",
                          justifyContent: "flex-start",
                        }),
                      }}
                    />
                  </div>
                </div>

                {/* city selection */}
                <div className="search-field-group">
                  <label className="search-label block mb-1 text-sm">
                    <FaCity className="inline mr-1" />
                    <span>{t("city")}</span>
                  </label>
                  <Select
                    className=" w-full "
                    classNamePrefix="react-select"
                    options={cityOptions}
                    value={
                      filteredCities
                        .map((city) => ({
                          value: city.id.toString(),
                          label: lang === "ar" ? city.name_ar : city.name_en,
                        }))
                        .find((option) => option.value === selectedCity) || null
                    }
                    onChange={(selectedOption) => {
                      setSelectedCity(selectedOption?.value || "");
                      setSelectedArea("");
                    }}
                    isDisabled={!selectedCountry || selectedCountry === ""}
                    placeholder={t("selectCity")}
                    isSearchable={false}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        opacity: state.isDisabled ? 0.6 : 1,

                        backgroundColor: state.isDisabled
                          ? "rgba(255, 255, 255, 0.4)"
                          : state.isFocused
                          ? "rgba(255, 255, 255, 0.15)"
                          : "#11132F",
                        border: state.isDisabled
                          ? "1px solid #555"
                          : state.isFocused
                          ? "1px solid #f9c835"
                          : "1px solid #332d7e",
                        borderRadius: 8,
                        transition: "all 0.3s ease",
                        color: "#fff",
                        height: "53px",
                        outline: "none",
                        paddingLeft: "16px",
                        paddingRight: "40px",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: state.isFocused
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.3)",
                          borderColor: state.isFocused
                            ? "#f9c835"
                            : "rgba(255, 255, 255, 0.2)",
                        },
                      }),
                      dropdownIndicator: (base, state) => ({
                        ...base,
                        color: "#aaa",
                        position: "absolute",
                        right: "12px",
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),

                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#11132F",
                        border: "1px solid #f9c835",
                        borderRadius: 8,
                        marginTop: 4,
                        color: "white",
                        zIndex: 9999,
                        overflowX: "hidden",
                      }),

                      menuList: (base) => ({
                        ...base,
                        overflowX: "hidden",
                        overflowY: "visible",
                        maxHeight: "none",
                        paddingRight: 0,
                      }),
                      menuList: (provided) => ({
                        ...provided,

                        scrollbarColor: "#007ACC transparent", // scroller color
                        scrollbarWidth: "thin",
                      }),
                      option: (base, state) => ({
                        ...base,
                        whiteSpace: "nowrap",
                        overflowX: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        color: state.data.value === "" ? "#fff" : "#fff",

                        backgroundColor: state.isFocused
                          ? "#007ACC"
                          : "#11132f",
                        textAlign: "left", // text left
                        paddingLeft: "12px",
                        ":active": {
                          backgroundColor: "#007ACC",
                        },
                      }),

                      singleValue: (provided, state) => ({
                        ...provided,
                        opacity: state.isDisabled ? 0.5 : 1,
                        transition: "opacity 300ms",
                        color: "#fff",
                      }),
                      placeholder: (base, state) => ({
                        ...base,
                        // color: "#fff",
                        marginLeft: 0,
                        paddingLeft: 0,
                        color: state.isDisabled ? "#fff" : "#fff",
                      }),

                      valueContainer: (base) => ({
                        ...base,
                        paddingLeft: "0px",
                        justifyContent: "flex-start",
                      }),
                    }}
                  />
                </div>
                {/* Area Select */}
                <div className="search-field-group">
                  <label className="search-label block mb-1 text-sm">
                    <FaMapPin className="inline mr-1" />
                    <span>{t("area")}</span>
                  </label>
                  <Select
                    className="w-full"
                    classNamePrefix="react-select"
                    options={[
                      { value: "", label: t("select_area") },
                      ...filteredAreas.map((area) => ({
                        value: area.id.toString(),
                        label: lang === "ar" ? area.name_ar : area.name_en,
                      })),
                    ]}
                    value={
                      filteredAreas
                        .map((area) => ({
                          value: area.id.toString(),
                          label: lang === "ar" ? area.name_ar : area.name_en,
                        }))
                        .find((option) => option.value === selectedArea) || {
                        value: "",
                        label: t("select_area"),
                      }
                    }
                    onChange={(selectedOption) =>
                      setSelectedArea(selectedOption?.value || "")
                    }
                    isDisabled={!selectedCity}
                    placeholder={t("selectArea")}
                    isSearchable={false}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        opacity: state.isDisabled ? 0.6 : 1,
                        backgroundColor: state.isDisabled
                          ? "rgba(255, 255, 255, 0.4)"
                          : state.isFocused
                          ? "rgba(255, 255, 255, 0.15)"
                          : "#11132F",
                        border: state.isDisabled
                          ? "1px solid #555"
                          : state.isFocused
                          ? "1px solid #f9c835"
                          : "1px solid #332d7e",
                        borderRadius: 8,
                        transition: "all 0.3s ease",
                        color: "#fff",
                        height: "53px",
                        outline: "none",
                        paddingLeft: "16px",
                        paddingRight: "40px",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: state.isFocused
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.3)",
                          borderColor: state.isFocused
                            ? "#f9c835"
                            : "rgba(255, 255, 255, 0.2)",
                        },
                      }),
                      dropdownIndicator: (base, state) => ({
                        ...base,
                        color: "#aaa",
                        position: "absolute",
                        right: "12px",
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#11132F",
                        border: "1px solid #f9c835",
                        borderRadius: 8,
                        marginTop: 4,
                        color: "white",
                        zIndex: 9999,
                        overflowX: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        overflowX: "hidden",
                        overflowY: "visible",
                        maxHeight: "none",
                        paddingRight: 0,
                      }),
                      menuList: (provided) => ({
                        ...provided,

                        scrollbarColor: "#007ACC transparent", // scroller color
                        scrollbarWidth: "thin",
                      }),
                      option: (base, state) => ({
                        ...base,
                        whiteSpace: "nowrap",
                        overflowX: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        color: "#fff",
                        backgroundColor: state.isFocused
                          ? "#007ACC"
                          : "#11132f",
                        textAlign: "left", // text left
                        paddingLeft: "12px",
                        ":active": {
                          backgroundColor: "#007ACC",
                        },
                      }),
                      singleValue: (provided, state) => ({
                        ...provided,
                        opacity: state?.isDisabled ? 0.5 : 1,
                        transition: "opacity 300ms",
                        color: "#fff",
                      }),
                      placeholder: (base, state) => ({
                        ...base,
                        marginLeft: 0,
                        paddingLeft: 0,
                        color: state.isDisabled ? "#fff" : "#fff",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        paddingLeft: "0px",
                        justifyContent: "flex-start",
                      }),
                    }}
                  />
                  {/* property type select */}
                </div>

                {/* property type select */}
                <div className="search-field-group">
                  <label className="search-label block mb-1 text-sm">
                    <FaHome className="inline mr-1" />
                    <span>{t("propertyType")}</span>
                  </label>
                  <Select
                    className="w-full"
                    classNamePrefix="react-select"
                    options={propertyTypeOptions}
                    value={
                      propertyTypeOptions
                        .flatMap((option) =>
                          option.options ? option.options : [option]
                        )
                        .find(
                          (option) => option.value === selectedPropertyType
                        ) || null
                    }
                    onChange={(selectedOption) =>
                      handlePropertyTypeChange({
                        target: { value: selectedOption?.value || "" },
                      })
                    }
                    isDisabled={!selectedCategory}
                    placeholder={t("selectType")}
                    isSearchable={false}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        opacity: state.isDisabled ? 0.6 : 1,
                        backgroundColor: state.isDisabled
                          ? "rgba(255, 255, 255, 0.4)"
                          : state.isFocused
                          ? "rgba(255, 255, 255, 0.15)"
                          : "#11132F",
                        border: state.isDisabled
                          ? "1px solid #555"
                          : state.isFocused
                          ? "1px solid #f9c835"
                          : "1px solid #332d7e",
                        borderRadius: 8,
                        transition: "all 0.3s ease",
                        color: "#fff",
                        height: "53px",
                        outline: "none",
                        paddingLeft: "16px",
                        paddingRight: "40px",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: state.isFocused
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.3)",
                          borderColor: state.isFocused
                            ? "#f9c835"
                            : "rgba(255, 255, 255, 0.2)",
                        },
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: "#aaa",
                        position: "absolute",
                        right: "12px",
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#11132F",
                        border: "1px solid #f9c835",
                        borderRadius: 8,
                        marginTop: 4,
                        color: "white",
                        zIndex: 9999,
                        overflowX: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        overflowX: "hidden",
                        overflowY: "visible",
                        maxHeight: "none",
                        paddingRight: 0,
                      }),
                      menuList: (provided) => ({
                        ...provided,

                        scrollbarColor: "#007ACC transparent", // scroller color
                        scrollbarWidth: "thin",
                      }),
                      option: (base, state) => ({
                        ...base,
                        whiteSpace: "nowrap",
                        overflowX: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        color: "#fff",
                        textAlign: "left", // text left
                        paddingLeft: "12px",
                        backgroundColor: state.isFocused
                          ? "#007ACC"
                          : "#11132f",
                        ":active": {
                          backgroundColor: "#007ACC",
                        },
                      }),
                      groupHeading: (base) => ({
                        ...base,
                        color: "#f9c835",
                        fontWeight: "bold",
                        textAlign: "left", //text left
                        fontSize: "0.9rem",
                        padding: "8px 12px 4px",
                        cursor: "default",
                        textTransform: "none",
                      }),
                      singleValue: (provided, state) => ({
                        ...provided,
                        opacity: state?.isDisabled ? 0.5 : 1,
                        transition: "opacity 300ms",
                        color: "#fff",
                      }),
                      placeholder: (base, state) => ({
                        ...base,
                        marginLeft: 0,
                        paddingLeft: 0,
                        color: state.isDisabled ? "#fff" : "#fff",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        paddingLeft: "0px",
                        justifyContent: "flex-start",
                      }),
                    }}
                  />
                </div>

                {/* Label for the budget range with an icon */}
                <div className="search-field-group">
                  <label className="search-label block mb-1 text-sm">
                    <FaCoins className="inline mr-1" />
                    <span>{t("minPrice")}</span>
                  </label>
                  <FormattedNumberInput
                    className="search-input property-type w-full text-sm "
                    value={minPrice}
                    onChange={setMinPrice}
                    placeholder={t("minPrice")}
                    id="minPrice"
                  />
                </div>
                {/* Container for minimum and maximum price inputs */}

                <div className="search-field-group">
                  <label className="search-label block mb-1 text-sm">
                    <FaCoins className="inline mr-1" />
                    <span>{t("maxPrice")}</span>
                  </label>

                  <FormattedNumberInput
                    className="search-input property-type w-full text-sm py-2"
                    value={maxPrice}
                    onChange={setMaxPrice}
                    placeholder={t("maxPrice")}
                    id="maxPrice"
                  />
                </div>
                <div className=" search-btn-container pl-4">
                  <button
                    type="button"
                    className="search-btn w-full bg-yellow-200 "
                    onClick={handleBrowseClick}
                  >
                    <FaSearch />
                    <span>{t("browse")}</span>
                  </button>
                </div>
              </form>
              {/* Action Buttons */}
              <div className="action-btn-container">
                <button
                  onClick={openAddUnitModal}
                  className="search-btn add-unit-btn"
                  style={{ backgroundColor: "#F9C835", color: "#121525" }}
                >
                  <FaPlus />
                  <span>{t("Addyourunitwithusforsale")}</span>
                  <div className="search-btn-glow absolute inset-0 opacity-0 pointer-events-none"></div>
                </button>
                <button
                  onClick={openContactModal}
                  className="contact-us-btn w-11/12 md:w-1/4 lg:w-1/6 py-4 px-5 rounded-lg text-lg font-semibold flex items-center justify-center gap-3  transition-all duration-300 transform hover:scale-105"
                  style={{ backgroundColor: "#332d7e", color: "white" }}
                >
                  <FaEnvelope />
                  <span>{t("contactUs")}</span>
                  <div className="search-btn-glow absolute inset-0 opacity-0 pointer-events-none"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Down Arrow */}
      <a
        className="scroll-down-arrow relative flex justify-center cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          const section = document.getElementById("hot-offers");
          if (section) {
            const offset = 150;
            const topPosition =
              section.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: topPosition, behavior: "smooth" });
          }
        }}
      >
        <div className="flex items-center gap-2">
          <FaFire style={{ color: "#ff0000", fontSize: "1.3rem" }} />
          <div className="flex items-center gap-2">
            <span
              style={{
                color: "#ff0000",
                fontWeight: "800",
                fontSize: "1.3rem",
              }}
            >
              {t("hot")}
            </span>
            <div className="chevron-container">
              <div className="chevron"></div>
              <div className="chevron"></div>
              <div className="chevron"></div>
            </div>
            <span
              style={{
                color: "var(--accent-yellow)",
                fontWeight: "800",
                fontSize: "1.3rem",
              }}
            >
              {t("offers")}
            </span>
          </div>
        </div>
      </a>

      <TrustedDevelopers developers={pageData.developers} />
    </section>
  );
};

export default Hero;
